import { h, Host } from "@stencil/core";
import { calculateOverlayDimensions, transformElement } from "./utils";
import { debounce } from "../../utils/debounce";
import { throttle } from "../../utils/throttle";
/**
 * @slot (default) - Content of the popover
 * @slot trigger - The trigger element
 */
export class Popover {
    constructor() {
        this.dimensions = null;
        this.isOpened = false;
        this.controlled = false;
        this.placement = "top";
        this.opened = false;
        this.closeOnSelect = true;
        this.targetElement = null;
    }
    domNode;
    overlayElement;
    triggerElement;
    onTargetChange() {
        if (this.opened) {
            this.updateOverlayPlacementDebounced();
        }
    }
    onPlacementChange() {
        if (this.opened) {
            this.updateOverlayPlacementDebounced();
        }
    }
    onOpenedChange() {
        if (this.opened) {
            this.addEventListeners();
            this.updateOverlayPlacementDebounced();
        }
        else {
            this.removeEventListeners();
            this.dimensions = null;
            this.isOpened = false;
        }
    }
    onDimensionsChange() {
        if (!this.opened || this.dimensions === null) {
            this.isOpened = false;
            return;
        }
        transformElement(this.overlayElement, `${this.dimensions.left}px`, `${this.dimensions.top}px`);
        if (!this.isOpened) {
            window.setTimeout(() => {
                if (!this.opened || this.dimensions === null) {
                    return;
                }
                this.isOpened = true;
            }, 10);
        }
    }
    updateIsOpened() {
        if (!this.isOpened) {
            return;
        }
        this.updateOverlayPlacementDebounced();
    }
    /** Fired when the popover opens. */
    uxShow;
    /** Fired when the popover closes. */
    uxHide;
    /** Show the popover */
    async show() {
        if (this.opened) {
            return;
        }
        const event = this.uxShow.emit();
        if (event.defaultPrevented) {
            return;
        }
        this.opened = true;
    }
    /** Hide the popover */
    async hide() {
        if (!this.opened) {
            return;
        }
        const event = this.uxHide.emit();
        if (event.defaultPrevented) {
            return;
        }
        this.opened = false;
    }
    /** Update the popver panel placemnt  */
    async updatePlacement() {
        if (!this.opened) {
            return;
        }
        this.updateOverlayPlacementThrottled();
    }
    componentDidLoad() {
        this.onOpenedChange();
    }
    disconnectedCallback() {
        this.removeEventListeners();
    }
    // -------------------------------------
    // Private methods
    // -------------------------------------
    updateOverlayPlacement = () => {
        const { opened, placement, overlayElement } = this;
        if (!opened || overlayElement === null || overlayElement === undefined) {
            return;
        }
        const targetElement = this.getTargetElement();
        const overlayRect = overlayElement.getBoundingClientRect();
        if (targetElement === null || placement === "center") {
            this.dimensions = {
                left: 0.5 * (window.innerWidth - overlayRect.width),
                top: 0.5 * (window.innerHeight - overlayRect.height),
                width: overlayRect.width,
                height: overlayRect.height,
                placement: "center"
            };
            return;
        }
        const targetRect = targetElement.getBoundingClientRect();
        this.dimensions = calculateOverlayDimensions(targetRect, overlayRect, placement);
    };
    updateOverlayPlacementDebounced = debounce(this.updateOverlayPlacement, 20);
    updateOverlayPlacementThrottled = throttle(this.updateOverlayPlacement, 20);
    addEventListeners = () => {
        this.removeEventListeners();
        if (this.controlled) {
            return;
        }
        window.addEventListener("scroll", this.handleViewportChange);
        window.addEventListener("resize", this.handleViewportChange);
        document.addEventListener("mousedown", this.handleDocumentMouseDown);
        document.addEventListener("keydown", this.handleDocumentKeyDown);
        this.overlayElement.addEventListener("uxSelect", this.handleListSelect);
        // @ts-expect-error Check for typing
        this.overlayElement.addEventListener("uxClosePopover", this.handleClosePopover);
    };
    removeEventListeners = () => {
        window.removeEventListener("scroll", this.handleViewportChange);
        window.removeEventListener("resize", this.handleViewportChange);
        document.removeEventListener("mousedown", this.handleDocumentMouseDown);
        document.removeEventListener("keydown", this.handleDocumentKeyDown);
        this.overlayElement.removeEventListener("uxSelect", this.handleListSelect);
        // @ts-expect-error Check for typing
        this.overlayElement.removeEventListener("uxClosePopover", this.handleClosePopover);
    };
    // -------------------------------------
    // Event Handlers
    // -------------------------------------
    handleViewportChange = () => {
        if (this.opened) {
            this.updateOverlayPlacementThrottled();
        }
    };
    handleDocumentKeyDown = (event) => {
        if (event.key === "Escape") {
            this.hide();
            return;
        }
        const targetElement = this.getTargetElement();
        if (event.key === "Tab") {
            setTimeout(() => {
                if (document.activeElement && document.activeElement.closest(targetElement.tagName) !== targetElement) {
                    this.hide();
                    return;
                }
            });
        }
        if (["ArrowDown", "ArrowUp", "Home", "End"].includes(event.key)) {
            event.preventDefault();
        }
    };
    getTargetElement = () => {
        return this.targetElement instanceof HTMLElement ? this.targetElement : this.triggerElement || null;
    };
    handleDocumentMouseDown = (event) => {
        if (!this.getTargetElement().contains(event.target) && !this.domNode.contains(event.target)) {
            requestAnimationFrame(() => this.hide());
        }
    };
    handleKeyDown = (event) => {
        if (!this.opened && ["ArrowDown", "ArrowUp"].includes(event.key)) {
            this.show();
            event.preventDefault();
            event.stopPropagation();
        }
    };
    handleTriggerClick = () => {
        if (this.opened) {
            this.hide();
        }
        else {
            this.show();
        }
    };
    handleClosePopover = (event) => {
        event.stopPropagation();
        this.hide();
    };
    handleListSelect = () => {
        if (this.closeOnSelect) {
            this.hide();
        }
    };
    render() {
        const { opened, dimensions, isOpened } = this;
        const baseClass = "ux-popover";
        const placement = dimensions !== null ? dimensions.placement : this.placement;
        return (h(Host, { key: 'b7f1dec9e85ffcd12d829b3ae8a5f8115e158e5c', onKeyDown: this.handleKeyDown }, h("div", { key: 'ecd21fa409227aa9e356130af5c994712c45c1f2', class: `${baseClass}__trigger`, ref: (el) => (this.triggerElement = el), onClick: this.handleTriggerClick }, h("slot", { key: 'c249f38fed5295bde1ceb9a5cfd30081fc257556', name: "trigger" })), h("div", { key: 'a1a34a655818539d8af2b94cb41979e7d15c18c8', class: {
                [`${baseClass}__overlay`]: true,
                [`${baseClass}__overlay--opening`]: opened && !isOpened,
                [`${baseClass}__overlay--opened`]: opened && isOpened
            },
            // @ts-expect-error Check for typing
            ref: (el) => (this.overlayElement = el), "data-popover-placement": placement, style: opened ? undefined : { display: "none" } }, h("div", { key: '0b1c66618438ff733852bbe45f2d7ee85c415faa', class: `${baseClass}__container` }, h("slot", { key: 'b52260b98e2ea5394f4368aa6ca037eb25cac2a5' })))));
    }
    static get is() { return "ux-popover"; }
    static get originalStyleUrls() {
        return {
            "$": ["./popover.scss"]
        };
    }
    static get styleUrls() {
        return {
            "$": ["popover.css"]
        };
    }
    static get properties() {
        return {
            "controlled": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Indicates whether the popover state is controlled by another component"
                },
                "attribute": "controlled",
                "reflect": true,
                "defaultValue": "false"
            },
            "placement": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "PopoverPlacementType",
                    "resolved": "\"bottom\" | \"bottom-end\" | \"bottom-start\" | \"center\" | \"left\" | \"left-end\" | \"left-start\" | \"right\" | \"right-end\" | \"right-start\" | \"top\" | \"top-end\" | \"top-start\"",
                    "references": {
                        "PopoverPlacementType": {
                            "location": "import",
                            "path": "./interface",
                            "id": "src/components/popover/interface.ts::PopoverPlacementType"
                        }
                    }
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Placement of the popover"
                },
                "attribute": "placement",
                "reflect": true,
                "defaultValue": "\"top\""
            },
            "opened": {
                "type": "boolean",
                "mutable": true,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Indicates whether the popover should be open or not"
                },
                "attribute": "opened",
                "reflect": true,
                "defaultValue": "false"
            },
            "closeOnSelect": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Set to `true` to automatically close the popover when a list item is selected."
                },
                "attribute": "close-on-select",
                "reflect": false,
                "defaultValue": "true"
            },
            "targetElement": {
                "type": "unknown",
                "mutable": false,
                "complexType": {
                    "original": "HTMLElement",
                    "resolved": "HTMLElement",
                    "references": {
                        "HTMLElement": {
                            "location": "global",
                            "id": "global::HTMLElement"
                        }
                    }
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "The element used to align the popover"
                },
                "defaultValue": "null"
            }
        };
    }
    static get states() {
        return {
            "dimensions": {},
            "isOpened": {}
        };
    }
    static get events() {
        return [{
                "method": "uxShow",
                "name": "uxShow",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": "Fired when the popover opens."
                },
                "complexType": {
                    "original": "any",
                    "resolved": "any",
                    "references": {}
                }
            }, {
                "method": "uxHide",
                "name": "uxHide",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": "Fired when the popover closes."
                },
                "complexType": {
                    "original": "any",
                    "resolved": "any",
                    "references": {}
                }
            }];
    }
    static get methods() {
        return {
            "show": {
                "complexType": {
                    "signature": "() => Promise<void>",
                    "parameters": [],
                    "references": {
                        "Promise": {
                            "location": "global",
                            "id": "global::Promise"
                        }
                    },
                    "return": "Promise<void>"
                },
                "docs": {
                    "text": "Show the popover",
                    "tags": []
                }
            },
            "hide": {
                "complexType": {
                    "signature": "() => Promise<void>",
                    "parameters": [],
                    "references": {
                        "Promise": {
                            "location": "global",
                            "id": "global::Promise"
                        }
                    },
                    "return": "Promise<void>"
                },
                "docs": {
                    "text": "Hide the popover",
                    "tags": []
                }
            },
            "updatePlacement": {
                "complexType": {
                    "signature": "() => Promise<void>",
                    "parameters": [],
                    "references": {
                        "Promise": {
                            "location": "global",
                            "id": "global::Promise"
                        }
                    },
                    "return": "Promise<void>"
                },
                "docs": {
                    "text": "Update the popver panel placemnt",
                    "tags": []
                }
            }
        };
    }
    static get elementRef() { return "domNode"; }
    static get watchers() {
        return [{
                "propName": "targetElement",
                "methodName": "onTargetChange"
            }, {
                "propName": "placement",
                "methodName": "onPlacementChange"
            }, {
                "propName": "opened",
                "methodName": "onOpenedChange"
            }, {
                "propName": "dimensions",
                "methodName": "onDimensionsChange"
            }, {
                "propName": "isOpened",
                "methodName": "updateIsOpened"
            }];
    }
}
//# sourceMappingURL=popover.js.map
